import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    Tab,
    Tabs,
    Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import UtilService from "../../../services/UtilService";
import Item from "../../components/Item";
import KeywordBox from "../../components/KeywordBox";
import CustomButton from "../../components/CustomButton";
import ConfirmDialog from "../../components/ConfirmDialog";
import SpeedRankService from "./SpeedRankService";
import SpeedRankPuzzlePanel from "./SpeedRankPuzzlePanel";
import Logger from "../../../services/Logger";
import { speedRankPuzzleDetailPath } from "./speedRankPaths";

const STORAGE_KEY = "admin.speedRank.searchOptions";
const TAB_KEY = "admin.speedRank.tab";
const PAGE_TABS = new Set(["player", "puzzle"]);
const PAGE_SIZE_OPTIONS = new Set([15, 25, 50, 100]);
const DEFAULT_SORT = [{ field: "bestSeconds", sort: "asc" }];

const TEST_FILTER_OPTIONS = [
    { value: "all", label: "List all" },
    { value: "testers", label: "Tester only" },
    { value: "exclude", label: "Exclude testers" },
];

const PUZZLE_TYPE_OPTIONS = [
    { value: "", label: "All types" },
    { value: "JIGSAW", label: "Jigsaw" },
    { value: "SLIDING", label: "Sliding" },
];

/** Sliding catalog: 4×4 / 6×6 × Easy · Medium · Hard. */
const SLIDING_MODE_OPTIONS = [
    { value: "4:EASY", label: "4×4 · Easy" },
    { value: "4:MEDIUM", label: "4×4 · Medium" },
    { value: "4:HARD", label: "4×4 · Hard" },
    { value: "6:EASY", label: "6×6 · Easy" },
    { value: "6:MEDIUM", label: "6×6 · Medium" },
    { value: "6:HARD", label: "6×6 · Hard" },
];

/**
 * Jigsaw catalog (tile size ↔ difficulty): 6 Easy, 9 Medium, 12 Hard (144),
 * 16 Pro (256), 24 Elite (576), 32 Insane (1024).
 */
const JIGSAW_MODE_OPTIONS = [
    { value: "6:EASY", label: "6×6 · Easy (36)" },
    { value: "9:MEDIUM", label: "9×9 · Medium (81)" },
    { value: "12:HARD", label: "12×12 · Hard (144)" },
    { value: "16:PRO", label: "16×16 · Pro (256)" },
    { value: "24:ELITE", label: "24×24 · Elite (576)" },
    { value: "32:INSANE", label: "32×32 · Insane (1024)" },
];

function modeOptionsForPuzzleType(puzzleType) {
    if (puzzleType === "SLIDING") {
        return [{ value: "", label: "All modes" }, ...SLIDING_MODE_OPTIONS];
    }
    if (puzzleType === "JIGSAW") {
        return [{ value: "", label: "All modes" }, ...JIGSAW_MODE_OPTIONS];
    }
    // All types: mode filter is ambiguous (e.g. 6:EASY exists on both) — pick type first.
    return [{ value: "", label: "All modes (pick a puzzle type)" }];
}

function defaultSearchOptions() {
    return {
        keyword: "",
        puzzleType: "JIGSAW",
        modeKey: "",
        testFilter: "exclude",
        page: 0,
        pageSize: 25,
        sort: DEFAULT_SORT,
    };
}

function loadSearchOptions() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return defaultSearchOptions();
        }
        const parsed = JSON.parse(raw);
        const pageSize = PAGE_SIZE_OPTIONS.has(parsed?.pageSize) ? parsed.pageSize : 25;
        const page = Number.isFinite(parsed?.page) && parsed.page >= 0 ? parsed.page : 0;
        const sort = Array.isArray(parsed?.sort) && parsed.sort.length > 0
            ? parsed.sort
            : DEFAULT_SORT;
        const puzzleType = PUZZLE_TYPE_OPTIONS.some((o) => o.value === parsed?.puzzleType)
            ? parsed.puzzleType
            : "JIGSAW";
        const allowedModes = new Set(
            modeOptionsForPuzzleType(puzzleType).map((o) => o.value).filter(Boolean)
        );
        const rawMode = typeof parsed?.modeKey === "string" ? parsed.modeKey : "";
        const modeKey = allowedModes.has(rawMode) ? rawMode : "";
        return {
            keyword: typeof parsed.keyword === "string" ? parsed.keyword : "",
            puzzleType,
            modeKey,
            testFilter: TEST_FILTER_OPTIONS.some((opt) => opt.value === parsed?.testFilter)
                ? parsed.testFilter
                : "exclude",
            page,
            pageSize,
            sort,
        };
    } catch {
        return defaultSearchOptions();
    }
}

function saveSearchOptions(options) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(options));
    } catch {
        /* ignore quota / private mode */
    }
}

function formatEpochMs(value) {
    if (value == null || value === 0) return "—";
    return UtilService.formatMDY(value) || "—";
}

function formatSeconds(seconds) {
    const n = Number(seconds);
    if (!Number.isFinite(n) || n < 0) return "—";
    const m = Math.floor(n / 60);
    const s = Math.floor(n % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}

function puzzleTypeLabel(type) {
    const match = PUZZLE_TYPE_OPTIONS.find((o) => o.value === type);
    return match?.label || type || "—";
}

function loadPageTab() {
    try {
        const stored = localStorage.getItem(TAB_KEY);
        return PAGE_TABS.has(stored) ? stored : "player";
    } catch {
        return "player";
    }
}

export default function SpeedRankMain() {
    const initialSearch = useMemo(() => loadSearchOptions(), []);
    const [pageTab, setPageTab] = useState(() => loadPageTab());
    const [keyword, setKeyword] = useState(initialSearch.keyword);
    const [puzzleType, setPuzzleType] = useState(initialSearch.puzzleType);
    const [modeKey, setModeKey] = useState(initialSearch.modeKey);
    const [testFilter, setTestFilter] = useState(initialSearch.testFilter);
    const modeOptions = useMemo(() => modeOptionsForPuzzleType(puzzleType), [puzzleType]);
    const [pageModel, setPageModel] = useState({
        page: initialSearch.page,
        pageSize: initialSearch.pageSize,
    });
    const [sort, setSort] = useState(initialSearch.sort);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [removeTarget, setRemoveTarget] = useState(null);
    const [error, setError] = useState(null);
    const [syncOpen, setSyncOpen] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState(null);
    const [seedOpen, setSeedOpen] = useState(false);
    const [seeding, setSeeding] = useState(false);
    const [removeAllOpen, setRemoveAllOpen] = useState(false);
    const [removingAll, setRemovingAll] = useState(false);
    const canSyncFromPlays = puzzleType === "JIGSAW" || puzzleType === "SLIDING";
    const syncTypeLabel = puzzleTypeLabel(puzzleType);

    useEffect(() => {
        saveSearchOptions({
            keyword,
            puzzleType,
            modeKey,
            testFilter,
            page: pageModel.page,
            pageSize: pageModel.pageSize,
            sort,
        });
    }, [keyword, puzzleType, modeKey, testFilter, pageModel, sort]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await SpeedRankService.list(pageModel, sort, {
                keyword,
                puzzleType,
                modeKey,
                testFilter,
            });
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || (result.error && !Array.isArray(result.content))
            ) {
                const msg =
                    result?.message
                    || result?.error
                    || "Failed to load speed ranks";
                setError(msg);
                setRows([]);
                setRowCount(0);
                Logger.warn("speed-rank load failed:", result);
                return;
            }
            const content = Array.isArray(result?.content) ? result.content : [];
            setRows(
                content.map((row, index) => ({
                    id: row.id || `row-${index}`,
                    rank: row.rank,
                    nickname: Boolean(row.testAccount)
                        ? "Anonymous"
                        : (row.nickname || "Player"),
                    isTest: Boolean(row.testAccount),
                    puzzleType: row.puzzleType || null,
                    modeLabel: row.modeLabel || row.modeKey || "—",
                    modeKey: row.modeKey || "",
                    bestSeconds: row.bestSeconds ?? 0,
                    puzzleTitle: row.puzzleTitle || "",
                    storyId: row.storyId || null,
                    updatedAt: row.updatedAt,
                }))
            );
            setRowCount(SpeedRankService.totalElements(result));
        } catch (err) {
            setRows([]);
            setRowCount(0);
            setError(err?.message || "Failed to load speed ranks");
            Logger.error("speed-rank load:", err);
        } finally {
            setLoading(false);
        }
    }, [keyword, puzzleType, modeKey, testFilter, pageModel, sort]);

    useEffect(() => {
        if (pageTab !== "player") return;
        load();
    }, [load, pageTab]);

    const handleConfirmRemove = useCallback(async (id) => {
        if (!id) return;
        setRemovingId(id);
        setError(null);
        try {
            const result = await SpeedRankService.remove(id);
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || result.code === "FAILURE"
                || result.error
            ) {
                setError(result?.message || result?.error || "Failed to remove row");
                return;
            }
            await load();
        } catch (err) {
            setError(err?.message || "Failed to remove row");
            Logger.error("speed-rank remove:", err);
        } finally {
            setRemovingId(null);
            setRemoveTarget(null);
        }
    }, [load]);

    const handleSyncFromPlays = useCallback(async () => {
        if (!canSyncFromPlays || syncing) return;
        setSyncing(true);
        setError(null);
        setSyncMessage(null);
        try {
            const result = await SpeedRankService.syncFromPlays(puzzleType);
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || result.code === "FAILURE"
                || result.error
            ) {
                setError(result?.message || result?.error || "Failed to sync from plays");
                return;
            }
            const inserted = result.inserted ?? result.data?.inserted ?? 0;
            const improved = result.improved ?? result.data?.improved ?? 0;
            const unchanged = result.unchanged ?? result.data?.unchanged ?? 0;
            const skipped = result.skipped ?? result.data?.skipped ?? 0;
            setSyncMessage(
                `Synced ${syncTypeLabel}: ${inserted} inserted, ${improved} improved, `
                + `${unchanged} unchanged, ${skipped} skipped.`,
            );
            await load();
        } catch (err) {
            setError(err?.message || "Failed to sync from plays");
            Logger.error("speed-rank sync from plays:", err);
        } finally {
            setSyncing(false);
        }
    }, [canSyncFromPlays, load, puzzleType, syncTypeLabel, syncing]);

    const handleRemoveAllTesters = useCallback(async () => {
        if (removingAll) return;
        setRemovingAll(true);
        setError(null);
        setSyncMessage(null);
        try {
            const result = await SpeedRankService.removeAllTesters();
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || result.code === "FAILURE"
                || result.error
            ) {
                setError(result?.message || result?.error || "Failed to remove tester records");
                return;
            }
            const deleted = result.deleted ?? result.data?.deleted ?? 0;
            setSyncMessage(`Removed ${deleted} tester speed-rank row(s).`);
            await load();
        } catch (err) {
            setError(err?.message || "Failed to remove tester records");
            Logger.error("speed-rank remove all testers:", err);
        } finally {
            setRemovingAll(false);
        }
    }, [load, removingAll]);

    const handleSeedSlidingTesters = useCallback(async () => {
        if (seeding) return;
        setSeeding(true);
        setError(null);
        setSyncMessage(null);
        try {
            const result = await SpeedRankService.seedSlidingTesters();
            if (
                result == null
                || (typeof result.status === "number" && result.status >= 400)
                || result.code === "FAILURE"
                || result.error
            ) {
                setError(result?.message || result?.error || "Failed to seed sliding testers");
                return;
            }
            const speedUpserted = result.speedUpserted ?? result.data?.speedUpserted ?? 0;
            const scoreUpserted = result.scoreUpserted ?? result.data?.scoreUpserted ?? 0;
            const winners = result.winnerSecondsByMode ?? result.data?.winnerSecondsByMode;
            const winnerHint = winners && typeof winners === "object"
                ? ` winners ${Object.entries(winners).map(([k, v]) => `${k}=${v}s`).join(", ")}`
                : "";
            setSyncMessage(
                `Seeded sliding testers: ${speedUpserted} speed rows, `
                + `${scoreUpserted} score rows.${winnerHint}`,
            );
            const nextFilter = testFilter === "exclude" ? "all" : testFilter;
            const alreadyOnSliding = puzzleType === "SLIDING" && testFilter === nextFilter;
            setPuzzleType("SLIDING");
            setTestFilter(nextFilter);
            setModeKey("");
            setPageModel((prev) => ({ ...prev, page: 0 }));
            if (alreadyOnSliding) {
                await load();
            }
        } catch (err) {
            setError(err?.message || "Failed to seed sliding testers");
            Logger.error("speed-rank seed sliding testers:", err);
        } finally {
            setSeeding(false);
        }
    }, [load, puzzleType, seeding, testFilter]);

    const columns = useMemo(
        () => [
            {
                field: "rank",
                headerName: "Rank",
                flex: 0.5,
                minWidth: 64,
                sortable: false,
            },
            {
                field: "nickname",
                headerName: "Nickname",
                flex: 1,
                minWidth: 120,
                sortable: false,
            },
            {
                field: "isTest",
                headerName: "Is test",
                flex: 0.6,
                minWidth: 80,
                sortable: false,
                align: "center",
                headerAlign: "center",
                renderCell: (params) =>
                    params.value ? (
                        <Chip size="small" color="warning" label="Yes" />
                    ) : (
                        <Chip size="small" variant="outlined" label="No" />
                    ),
            },
            {
                field: "puzzleType",
                headerName: "Puzzle",
                flex: 0.7,
                minWidth: 100,
                sortable: false,
                renderCell: (params) =>
                    params.value ? (
                        <Chip size="small" label={puzzleTypeLabel(params.value)} />
                    ) : (
                        "—"
                    ),
            },
            {
                field: "modeLabel",
                headerName: "Mode",
                flex: 1,
                minWidth: 120,
                sortable: false,
            },
            {
                field: "bestSeconds",
                headerName: "Best time",
                flex: 0.7,
                minWidth: 90,
                valueFormatter: (value) => formatSeconds(value),
            },
            {
                field: "puzzleTitle",
                headerName: "Puzzle title",
                flex: 1.2,
                minWidth: 140,
                sortable: false,
                renderCell: (params) => {
                    const title = params.value || params.row?.puzzleTitle || "";
                    const href = speedRankPuzzleDetailPath(params.row?.puzzleType, params.row?.storyId);
                    if (!title) {
                        return "—";
                    }
                    if (!href) {
                        return title;
                    }
                    return (
                        <Link
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            underline="hover"
                            onClick={(event) => event.stopPropagation()}
                            sx={{ fontWeight: 500 }}
                        >
                            {title}
                        </Link>
                    );
                },
            },
            {
                field: "updatedAt",
                headerName: "Updated",
                flex: 0.7,
                minWidth: 110,
                sortable: false,
                valueFormatter: (value) => formatEpochMs(value),
            },
            {
                field: "actions",
                headerName: "Actions",
                flex: 0.8,
                minWidth: 120,
                sortable: false,
                filterable: false,
                align: "center",
                headerAlign: "center",
                display: "flex",
                renderCell: (params) => {
                    if (!params.row?.isTest) {
                        return null;
                    }
                    const removeBusy = removingId === params.row.id;
                    const busy = removeBusy || loading;
                    return (
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ width: "100%", height: "100%" }}
                        >
                            <CustomButton
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={busy}
                                onClick={() => setRemoveTarget({
                                    id: params.row.id,
                                    nickname: params.row.nickname,
                                })}
                            >
                                {removeBusy ? "Removing…" : "Remove"}
                            </CustomButton>
                        </Stack>
                    );
                },
            },
        ],
        [loading, removingId]
    );

    return (
        <Box sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs
                    value={pageTab}
                    onChange={(_, value) => {
                        if (!PAGE_TABS.has(value)) return;
                        setPageTab(value);
                        try {
                            localStorage.setItem(TAB_KEY, value);
                        } catch {
                            /* ignore */
                        }
                    }}
                    aria-label="Speed rank sections"
                >
                    <Tab label="Player" value="player" />
                    <Tab label="Puzzle" value="puzzle" />
                </Tabs>
            </Box>

            {pageTab === "puzzle" ? (
                <SpeedRankPuzzlePanel puzzleType={puzzleType || "JIGSAW"} testFilter={testFilter} />
            ) : (
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                        <Item>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 2,
                                    alignItems: "center",
                                }}
                            >
                                <KeywordBox
                                    callback={(value) => {
                                        setKeyword(value);
                                        setPageModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
                                    }}
                                    initialValue={initialSearch.keyword}
                                />
                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                    <InputLabel id="sr-puzzle-type-label">Puzzle type</InputLabel>
                                    <Select
                                        labelId="sr-puzzle-type-label"
                                        label="Puzzle type"
                                        value={puzzleType}
                                        onChange={(e) => {
                                            const nextType = e.target.value;
                                            setPuzzleType(nextType);
                                            const allowed = new Set(
                                                modeOptionsForPuzzleType(nextType)
                                                    .map((o) => o.value)
                                                    .filter(Boolean)
                                            );
                                            if (!allowed.has(modeKey)) {
                                                setModeKey("");
                                            }
                                            setPageModel((prev) => ({ ...prev, page: 0 }));
                                        }}
                                    >
                                        {PUZZLE_TYPE_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value || "all"} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 220 }} disabled={!puzzleType}>
                                    <InputLabel id="sr-mode-label">Mode</InputLabel>
                                    <Select
                                        labelId="sr-mode-label"
                                        label="Mode"
                                        value={modeKey}
                                        onChange={(e) => {
                                            setModeKey(e.target.value);
                                            setPageModel((prev) => ({ ...prev, page: 0 }));
                                        }}
                                    >
                                        {modeOptions.map((opt) => (
                                            <MenuItem key={opt.value || "all-modes"} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel id="sr-test-filter-label">Testers</InputLabel>
                                    <Select
                                        labelId="sr-test-filter-label"
                                        label="Testers"
                                        value={testFilter}
                                        onChange={(e) => {
                                            setTestFilter(e.target.value);
                                            setPageModel((prev) => ({ ...prev, page: 0 }));
                                        }}
                                    >
                                        {TEST_FILTER_OPTIONS.map((opt) => (
                                            <MenuItem key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <CustomButton
                                    variant="outlined"
                                    onClick={() => load()}
                                    disabled={loading}
                                >
                                    Refresh
                                </CustomButton>
                                {canSyncFromPlays ? (
                                    <CustomButton
                                        variant="outlined"
                                        disabled={syncing || seeding || loading}
                                        onClick={() => setSyncOpen(true)}
                                    >
                                        {syncing ? "Syncing…" : "Sync from plays"}
                                    </CustomButton>
                                ) : null}
                                <CustomButton
                                    variant="outlined"
                                    disabled={seeding || syncing || removingAll || loading}
                                    onClick={() => setSeedOpen(true)}
                                >
                                    {seeding ? "Seeding…" : "Add tester play records"}
                                </CustomButton>
                                <CustomButton
                                    variant="outlined"
                                    color="error"
                                    disabled={removingAll || seeding || syncing || loading}
                                    onClick={() => setRemoveAllOpen(true)}
                                >
                                    {removingAll ? "Removing…" : "Remove all tester records"}
                                </CustomButton>
                            </Box>
                            {canSyncFromPlays ? (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                    Sync calculates personal bests from completed signed-in {syncTypeLabel} plays
                                    (started_at / finished_at). Existing faster PBs are kept.
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                                    Choose Jigsaw or Sliding to sync personal bests from play logs.
                                </Typography>
                            )}
                            {syncMessage ? (
                                <Typography color="success.main" variant="body2" sx={{ mt: 1 }}>
                                    {syncMessage}
                                </Typography>
                            ) : null}
                        </Item>
                    </Grid>
                    {error ? (
                        <Grid size={{ xs: 12 }}>
                            <Typography color="error" variant="body2">
                                {error}
                            </Typography>
                        </Grid>
                    ) : null}
                    <Grid size={{ xs: 12 }}>
                        <Item>
                            <DataGrid
                                autoHeight
                                rows={rows}
                                columns={columns}
                                rowCount={rowCount}
                                loading={loading}
                                paginationMode="server"
                                sortingMode="server"
                                pageSizeOptions={[15, 25, 50, 100]}
                                paginationModel={pageModel}
                                onPaginationModelChange={setPageModel}
                                sortModel={sort}
                                onSortModelChange={(model) => {
                                    setSort(model.length ? model : DEFAULT_SORT);
                                    setPageModel((prev) => ({ ...prev, page: 0 }));
                                }}
                                disableRowSelectionOnClick
                                sx={{ border: 0 }}
                            />
                        </Item>
                    </Grid>
                </Grid>
            )}

            <ConfirmDialog
                open={Boolean(removeTarget)}
                setOpen={(open) => {
                    if (!removingId) {
                        if (!open) setRemoveTarget(null);
                    }
                }}
                title="Remove tester speed rank?"
                message="Only tester rows can be removed. This deletes that personal best from the speed board. It does not delete the user account."
                targetName={removeTarget?.nickname || ""}
                targetId={removeTarget?.id}
                warningAlert="This cannot be undone from this page."
                callback={handleConfirmRemove}
            />
            <ConfirmDialog
                open={syncOpen}
                setOpen={setSyncOpen}
                title={`Sync ${syncTypeLabel} speed ranks`}
                targetName={syncTypeLabel}
                message={[
                    `Only ${syncTypeLabel} is rebuilt from completed plays. Other types stay as they are.`,
                    "For each signed-in user and mode, the fastest finished_at − started_at becomes the personal best.",
                    "Runs that are absurdly fast are skipped. Existing faster PBs are not overwritten.",
                ]}
                okText="Sync from plays"
                cancelText="Cancel"
                callback={handleSyncFromPlays}
            />
            <ConfirmDialog
                open={seedOpen}
                setOpen={setSeedOpen}
                title="Add sliding tester play records?"
                targetName="Anonymous × 10"
                message={[
                    "Creates or updates 10 test players. Nickname shown as Anonymous on speed boards.",
                    "Each mode gets its own time band (4×4 Easy ~1m … 6×6 Hard ~8–10m) with irregular gaps — not a fixed +10s ladder.",
                    "Puzzle titles come from recently published sliding boards (matched by mode when possible).",
                    "Also sets matching Sliding score-board rows (#1 score 10 … #10 score 1).",
                    "Re-running refreshes times and titles.",
                ]}
                okText="Seed testers"
                cancelText="Cancel"
                callback={handleSeedSlidingTesters}
            />
            <ConfirmDialog
                open={removeAllOpen}
                setOpen={setRemoveAllOpen}
                title="Remove all tester speed ranks?"
                message="Deletes every personal-best row owned by a test account. Real player rows are kept. This does not delete user accounts."
                warningAlert="This cannot be undone from this page."
                okText="Remove all testers"
                cancelText="Cancel"
                callback={handleRemoveAllTesters}
            />
        </Box>
    );
}
